import {useCallback, useEffect, useMemo, useRef} from 'react';

import {
    useAssessmentsCatalogQuery,
    usePopQuizAttemptStartMutation,
    usePopQuizItemSearchQuery,
} from 'src/generated/graphql';

import {MIN_QUIZ_QUESTIONS} from './constants';
import {RecommendedAssessmentEventPayload} from './events';
import {usePopQuizLifecycle, UsePopQuizLifecycleResult} from './lifecycle/use-pop-quiz-lifecycle';
import {mapRecommendedAssessment} from './map-recommended-assessment';
import {mapRelatedLectures} from './map-related-lectures';
import {useQuizOrchestrator} from './orchestrator/use-quiz-orchestrator';
import {usePopQuizEligibility} from './pop-quiz-eligibility';
import {selectRecommendedAssessment} from './select-recommended-assessment';
import {PopQuizItem, PopQuizModalProps, PopQuizRecommendedAssessment, QuizCandidate} from './types';

export interface UsePopQuizResult extends UsePopQuizLifecycleResult {
    quizData: QuizCandidate | null;
    recommendedAssessment: PopQuizRecommendedAssessment | undefined;
    canRequestDifferentQuiz: boolean;
}

export function usePopQuiz({
    areOtherModalsOpen,
    enablePopQuizPP,
    popQuizV2ExperienceEnabled,
}: PopQuizModalProps): UsePopQuizResult {
    const eligibility = usePopQuizEligibility();

    // Allow ?showPopQuiz on the URL to bypass all cap/de-dupe checks (dev/QA only).
    // ?quizId= additionally forces a specific (synthetic) quiz for QA, folded into the
    // orchestrator as its own top-ranked 'debug' candidate source.
    const debugSearchParams =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const forcePopQuiz = !!debugSearchParams?.has('showPopQuiz');
    const forcedQuizId = debugSearchParams?.get('quizId') ?? undefined;

    // Orchestrator owns "which quiz (and why) should we show right now" — the single seam for
    // "what quiz is active and why."
    const {
        quizData: orchestratorQuizData,
        hasAnotherCandidate,
        requestDifferentQuiz,
    } = useQuizOrchestrator({
        enablePopQuizPP,
        popQuizV2ExperienceEnabled,
        eligibility,
        forcePopQuiz,
        forcedQuizId,
    });

    // `isDebugQuiz` suppresses tracking/mutations in prod (see isDebugQuizSuppressed) for any
    // quiz shown under the ?showPopQuiz override
    const rawQuizData: QuizCandidate | null = useMemo(
        () => (orchestratorQuizData ? {...orchestratorQuizData, isDebugQuiz: forcePopQuiz} : null),
        [orchestratorQuizData, forcePopQuiz],
    );

    // Pre-fetch quiz items so they're ready when the prompt shows. relatedLectures is only
    // requested when popQuizV2ExperienceEnabled — control-group users don't pay for that payload.
    const {data: itemSearchData, isFetching: isItemSearchFetching} = usePopQuizItemSearchQuery(
        {
            quizId: String(rawQuizData?.quizId ?? ''),
            includeRelatedLectures: popQuizV2ExperienceEnabled,
        },
        {enabled: !!rawQuizData && enablePopQuizPP},
    );

    // Fetch the assessments catalog to find a related assessment for the course.
    const {data: assessmentsCatalogData, isFetching: isAssessmentsCatalogFetching} =
        useAssessmentsCatalogQuery(
            {},
            {enabled: !!rawQuizData && enablePopQuizPP && popQuizV2ExperienceEnabled},
        );
    const isAssessmentsCatalogSettled = !isAssessmentsCatalogFetching;

    const recommendedAssessment = useMemo(
        () =>
            popQuizV2ExperienceEnabled
                ? selectRecommendedAssessment(
                      (assessmentsCatalogData?.assessments ?? [])
                          .filter((a) => a != null)
                          .map(mapRecommendedAssessment),
                      rawQuizData?.courseTopicId ?? null,
                  )
                : undefined,
        [popQuizV2ExperienceEnabled, assessmentsCatalogData, rawQuizData],
    );

    // Shared by the Presented and LinkSelected tracking calls (inside the lifecycle hook).
    const assessmentEventPayload: RecommendedAssessmentEventPayload | undefined = useMemo(
        () =>
            recommendedAssessment
                ? {
                      id: Number(recommendedAssessment.id),
                      title: recommendedAssessment.title,
                      isBeta: recommendedAssessment.isBeta,
                      domainId: recommendedAssessment.domainId,
                  }
                : undefined,
        [recommendedAssessment],
    );

    // Map GQL items to the internal PopQuizItem shape, filtering to supported types.
    const supportedItems = useMemo((): PopQuizItem[] => {
        const rawItems = itemSearchData?.quizItemSearch?.items ?? [];
        return rawItems.reduce<PopQuizItem[]>((acc, item) => {
            if (item.__typename === 'MultipleChoiceQuizItem') {
                acc.push({
                    id: item.id,
                    text: item.text,
                    options: item.options,
                    isMultiSelect: false,
                    correctOptionIds: [item.correctOption.id],
                    explanation: item.explanation,
                    relatedLectures: mapRelatedLectures(item.relatedLectures),
                });
            } else if (item.__typename === 'MultiSelectQuizItem') {
                acc.push({
                    id: item.id,
                    text: item.text,
                    options: item.options,
                    isMultiSelect: true,
                    correctOptionIds: item.correctOptions.map((o) => o.id),
                    explanation: item.explanation,
                    relatedLectures: mapRelatedLectures(item.relatedLectures),
                });
            }
            return acc;
        }, []);
    }, [itemSearchData]);

    // Tracks the quizId the undersized-candidate bail-out has already fired for
    const lastBailedOutQuizIdRef = useRef<number | null>(null);

    // True once the current pick's items are fetched and too small to be an acceptable quiz
    const isSettledUndersized =
        !isItemSearchFetching &&
        supportedItems.length > 0 &&
        supportedItems.length < MIN_QUIZ_QUESTIONS;

    useEffect(() => {
        if (!rawQuizData || !isSettledUndersized) {
            return;
        }
        if (hasAnotherCandidate) {
            requestDifferentQuiz();
            return;
        }
        if (lastBailedOutQuizIdRef.current === rawQuizData.quizId) {
            return;
        }
        lastBailedOutQuizIdRef.current = rawQuizData.quizId;
        eligibility.markShownToday();
    }, [rawQuizData, isSettledUndersized, hasAnotherCandidate, requestDifferentQuiz, eligibility]);

    // The value handed to usePopQuizLifecycle (and returned below) — null while the current pick
    // is settled-and-undersized (being retried or already exhausted), so lifecycle only ever sees
    // `null` or an already-appropriately-sized candidate.
    const quizData: QuizCandidate | null = isSettledUndersized ? null : rawQuizData;

    const startAttemptMutation = usePopQuizAttemptStartMutation();
    const startAttempt = useCallback(
        async (quizId: string): Promise<string | undefined> => {
            const result = await startAttemptMutation.mutateAsync({quizId});
            return result?.quizAttemptStart?.id ?? undefined;
        },
        [startAttemptMutation],
    );

    const lifecycle = usePopQuizLifecycle({
        quizData: quizData ?? undefined,
        supportedItems,
        isItemSearchFetching,
        isAssessmentsCatalogSettled,
        areOtherModalsOpen,
        assessmentEventPayload,
        requestDifferentQuiz,
        eligibility,
        startAttempt,
    });

    const canRequestDifferentQuiz =
        popQuizV2ExperienceEnabled && hasAnotherCandidate && quizData?.source !== 'debug';

    return {
        ...lifecycle,
        quizData,
        recommendedAssessment,
        canRequestDifferentQuiz,
    };
}
